"use client";

import { useLanguage } from "@/app/contexts/languagecontext";
import enTranslations from "@/messages/en.json";
import myTranslations from "@/messages/my.json";
import thTranslations from "@/messages/th.json";
import { useTransition } from "react";

 
type Translations = any;

/**
 * Centralized translation hook for all pages
 * Usage: const { t } = useTranslations();
 *        <h1>{t('common.save')}</h1>  // "Save" or "သိမ်းဆည်းပါမည်"
 *
 *        // With namespace
 *        const { t } = useTranslations('category');
 *        <h1>{t('createNew')}</h1>  // "Create New Category"
 */
export function useTranslations(namespace?: string) {
  const { locale } = useLanguage();
  const [isPending, startTransition] = useTransition();

  // All translations
  const allTranslations: Record<string, Translations> = {
    en: enTranslations,
    my: myTranslations,
    th: thTranslations,
  };

  const getNestedValue = (obj: Translations, path: string): string => {
    const keys = path.split(".");
    let result: Translations = obj;

    for (const key of keys) {
      if (result && typeof result === "object" && key in result) {
        result = result[key];
      } else {
        return path; // Return key if not found
      }
    }

    return typeof result === "string" ? result : path;
  };

  const t = (key: string, params?: Record<string, string | number>): string => {
    const currentTranslations = allTranslations[locale] || allTranslations.en;

    // If namespace provided, look in that namespace first
    let translation: string;
    if (namespace) {
      const namespaceData = currentTranslations[namespace];
      if (namespaceData && typeof namespaceData === "object") {
        translation = getNestedValue(namespaceData, key);
      } else {
        translation = getNestedValue(
          currentTranslations,
          `${namespace}.${key}`,
        );
      }
    } else {
      translation = getNestedValue(currentTranslations, key);
    }

    // If not found in namespace, try common namespace as fallback
    if (translation === key && namespace && namespace !== "common") {
      const commonData = currentTranslations.common;
      if (commonData && typeof commonData === "object" && key in commonData) {
        translation = commonData[key];
      }
    }

    // Replace parameters like {count}, {name}, etc.
    if (params && typeof translation === "string") {
      Object.entries(params).forEach(([paramKey, paramValue]) => {
        translation = translation.replace(
          new RegExp(`\\{${paramKey}\\}`, "g"),
          String(paramValue),
        );
      });
    }

    return translation;
  };

  return { t, locale, isPending, startTransition };
}
